import { type BaseTicketComment } from "@mg/schemas/src/christo/catalyst";
import {
  ArrowsInSimple,
  ArrowsOutSimple,
  CircleNotch,
} from "@phosphor-icons/react";
import { Tooltip } from "@radix-ui/themes";
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import {
  type ImperativePanelGroupHandle,
  Panel,
  PanelGroup,
} from "react-resizable-panels";

import { ArtifactPaneCloseButton } from "./ArtifactPaneCloseButton";
import {
  AssetViewerPane,
  CommentPanel,
  type AssetFile,
  type AssetViewerPaneHandle,
  type DraftPin,
  type DetachedCommentPanelProps,
  type HighlightPosition,
} from "./AssetViewerPane";
import { KnowledgeRecordContextPanel } from "./KnowledgeRecordContextPanel";
import {
  PANE_HEADER_ICON_SIZE,
  PaneHeader,
  PaneHeaderIconButton,
} from "./PaneHeader";
import { ResizeHandle } from "./ResizeHandle";
import { VersionApprovalBadge } from "./VersionApprovalBadge";

import { findContextFileIndex } from "../chatUtils";
import { usePersistedLayout } from "../hooks/usePersistedLayout";
import { useTicketViewData } from "../hooks/useTicketView";
import {
  buildContextFileCitation,
  citationToTarget,
  getNavigableCitations,
  type CitationTarget,
  type TicketMessageCitation,
} from "../utils/commentSourceUtils";
import {
  visibleReviewComments,
  reviewCommentsForPanel,
} from "../utils/visibleComments";

const CITATION_HIGHLIGHT_DURATION_MS = 5000;
const COMMENT_HIGHLIGHT_DURATION_MS = 3000;

type PendingCitation = CitationTarget & { transient?: boolean };

export type ReviewArtworkContextData = {
  ticketId: string;
  comments: BaseTicketComment[] | undefined;
  isLoading: boolean;
  hasContextAssets: boolean;
  contextFiles: AssetFile[];
  latestReviewFiles: AssetFile[];
};

export type ReviewArtworkContextColumnsOptions = ReviewArtworkContextData & {
  contextCollapsed: boolean;
  onContextCollapsedChange: (collapsed: boolean) => void;
  /** Deep-link hydration: select this comment once comments load. */
  initialCommentId?: string | null;
  /** Push comment selection into the URL (PUSH navigation). */
  onCommentDeepLinkChange?: (commentId: string | null) => void;
  /** Strip an invalid comment segment from the URL (REPLACE navigation). */
  onInvalidCommentDeepLink?: () => void;
  onPinDragEnd?: (
    comment: BaseTicketComment,
    newRelX: number,
    newRelY: number,
  ) => void;
  onPasswordRequired?: (fileName: string) => void;
};

export function useReviewArtworkContextColumns(
  opts: ReviewArtworkContextColumnsOptions,
) {
  const {
    ticketId,
    comments,
    contextFiles,
    contextCollapsed,
    onContextCollapsedChange,
    hasContextAssets,
    initialCommentId,
    onCommentDeepLinkChange,
  } = opts;

  const [draftCitations, setDraftCitations] = useState<TicketMessageCitation[]>(
    [],
  );
  const [sourcePlacementMode, setSourcePlacementMode] = useState(false);
  const [draftContextSourcePin, setDraftContextSourcePin] =
    useState<DraftPin | null>(null);
  const [contextCitationPin, setContextCitationPin] = useState<DraftPin | null>(
    null,
  );

  const [selectedCommentId, setSelectedCommentId] = useState<string | null>(
    initialCommentId ?? null,
  );
  const [highlightPing, setHighlightPing] = useState<HighlightPosition | null>(
    null,
  );
  const [contextHighlight, setContextHighlight] =
    useState<HighlightPosition | null>(null);
  const [artworkHeaderControls, setArtworkHeaderControls] =
    useState<ReactNode | null>(null);
  const [contextHeaderControls, setContextHeaderControls] =
    useState<ReactNode | null>(null);
  const [selectedKnowledgeRecordId, setSelectedKnowledgeRecordId] = useState<
    string | null
  >(null);
  const [activeCitationIndex, setActiveCitationIndex] = useState(0);
  const [includeRejectedComments, setIncludeRejectedComments] = useState(false);

  const focusTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const contextHighlightTimerRef = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );
  const pendingCitationRef = useRef<PendingCitation | null>(null);
  const autoAppliedSelectionRef = useRef<{
    commentId: string;
    citationIndex: number;
  } | null>(null);
  const contextSourcePinCompleteRef = useRef<
    ((citation: TicketMessageCitation) => void) | null
  >(null);
  const contextPaneRef = useRef<AssetViewerPaneHandle>(null);
  const artworkPaneRef = useRef<AssetViewerPaneHandle>(null);
  const prevTicketIdRef = useRef(ticketId);

  const activeComments = useMemo(
    () => visibleReviewComments(comments),
    [comments],
  );

  const commentsForSelection = useMemo(
    () => reviewCommentsForPanel(comments, includeRejectedComments),
    [comments, includeRejectedComments],
  );

  const stableComments = useMemo(() => activeComments, [activeComments]);

  const [detachedCommentPanelProps, setDetachedCommentPanelProps] =
    useState<DetachedCommentPanelProps | null>(null);
  const onDetachedCommentPanelProps = useCallback(
    (props: DetachedCommentPanelProps | null) => {
      setDetachedCommentPanelProps(props);
    },
    [],
  );

  useEffect(() => {
    if (prevTicketIdRef.current === ticketId) return;
    prevTicketIdRef.current = ticketId;
    setSelectedCommentId(null);
    setHighlightPing(null);
    setContextHighlight(null);
    setDetachedCommentPanelProps(null);
    setDraftCitations([]);
    setSourcePlacementMode(false);
    setDraftContextSourcePin(null);
    setContextCitationPin(null);
    pendingCitationRef.current = null;
    contextSourcePinCompleteRef.current = null;
    setSelectedKnowledgeRecordId(null);
    setActiveCitationIndex(0);
    setIncludeRejectedComments(false);
    autoAppliedSelectionRef.current = null;
    onContextCollapsedChange(false);
  }, [ticketId, onContextCollapsedChange]);

  useEffect(() => {
    if (initialCommentId) return;
    if (!selectedCommentId && activeComments.length) {
      setSelectedCommentId(activeComments[0]._id ?? null);
    }
  }, [initialCommentId, selectedCommentId, activeComments]);

  const hydratedCommentRef = useRef<string | null>(null);
  useEffect(() => {
    if (!initialCommentId || !comments?.length) return;
    if (hydratedCommentRef.current === initialCommentId) return;
    const comment = comments.find((c) => c._id === initialCommentId);
    if (!comment) return;
    hydratedCommentRef.current = initialCommentId;
    setSelectedCommentId(initialCommentId);
    setActiveCitationIndex(0);
    if (comment.rel_x != null && comment.rel_y != null) {
      if (focusTimerRef.current) clearTimeout(focusTimerRef.current);
      setHighlightPing({
        x: comment.rel_x,
        y: comment.rel_y,
        page: comment.page_num,
      });
      focusTimerRef.current = setTimeout(
        () => setHighlightPing(null),
        COMMENT_HIGHLIGHT_DURATION_MS,
      );
    }
    requestAnimationFrame(() => {
      const el = document.querySelector(
        `[data-testid="comment-card-${initialCommentId}"]`,
      );
      el?.scrollIntoView({ block: "nearest", behavior: "smooth" });
    });
  }, [initialCommentId, comments]);

  useEffect(() => {
    if (!selectedCommentId) return;
    const stillVisible = commentsForSelection.some(
      (c) => c._id === selectedCommentId,
    );
    if (!stillVisible) {
      setSelectedCommentId(commentsForSelection[0]?._id ?? null);
    }
  }, [selectedCommentId, commentsForSelection]);

  useEffect(
    () => () => {
      if (focusTimerRef.current) clearTimeout(focusTimerRef.current);
      if (contextHighlightTimerRef.current) {
        clearTimeout(contextHighlightTimerRef.current);
      }
    },
    [],
  );

  const applyContextCitation = useCallback(
    (
      {
        pageNumber,
        position,
        sourceUrl,
        originalURL,
        videoStart,
        cellAnchor,
        knowledgeDataRecordId,
        transient = false,
      }: PendingCitation,
      options?: { transient?: boolean },
    ) => {
      const isTransient = options?.transient ?? transient ?? false;

      if (knowledgeDataRecordId) {
        setSelectedKnowledgeRecordId(knowledgeDataRecordId);
        setContextHighlight(null);
        setContextCitationPin(null);
        return;
      }

      setSelectedKnowledgeRecordId(null);
      const targetIdx = findContextFileIndex(
        contextFiles,
        sourceUrl,
        originalURL,
      );
      if (targetIdx >= 0) {
        contextPaneRef.current?.navigateToFile(targetIdx, pageNumber ?? 1);
      } else if (pageNumber != null) {
        contextPaneRef.current?.navigateToPage(pageNumber);
      }
      // Documents/images show a comment-style pin; video sources seek to
      // `videoStart`; spreadsheet sources surface the cited sheet/cells.
      if (position?.x != null && position?.y != null && pageNumber != null) {
        if (contextHighlightTimerRef.current) {
          clearTimeout(contextHighlightTimerRef.current);
          contextHighlightTimerRef.current = null;
        }
        setContextHighlight(null);
        setContextCitationPin(null);
        requestAnimationFrame(() => {
          setContextCitationPin({
            relX: position.x,
            relY: position.y,
            page: pageNumber,
          });
          if (isTransient) {
            contextHighlightTimerRef.current = setTimeout(
              () => setContextCitationPin(null),
              CITATION_HIGHLIGHT_DURATION_MS,
            );
          }
        });
      } else if (position || videoStart != null || cellAnchor != null) {
        setContextCitationPin(null);
        if (contextHighlightTimerRef.current) {
          clearTimeout(contextHighlightTimerRef.current);
          contextHighlightTimerRef.current = null;
        }
        setContextHighlight(null);
        requestAnimationFrame(() => {
          setContextHighlight({
            x: position?.x,
            y: position?.y,
            page: pageNumber,
            videoStart,
            cellAnchor,
          });
          if (isTransient) {
            contextHighlightTimerRef.current = setTimeout(
              () => setContextHighlight(null),
              CITATION_HIGHLIGHT_DURATION_MS,
            );
          }
        });
      }
    },
    [contextFiles],
  );

  useLayoutEffect(() => {
    if (!pendingCitationRef.current) return;
    if (contextCollapsed) return;
    const pending = pendingCitationRef.current;
    const flush = () => {
      if (!contextPaneRef.current) {
        requestAnimationFrame(flush);
        return;
      }
      pendingCitationRef.current = null;
      applyContextCitation(pending, { transient: pending.transient ?? false });
    };
    flush();
  }, [contextCollapsed, applyContextCitation]);

  const navigableCitationsForSelected = useMemo(() => {
    if (!selectedCommentId) return [];
    const comment = commentsForSelection.find(
      (c) => c._id === selectedCommentId,
    );
    return comment ? getNavigableCitations(comment) : [];
  }, [commentsForSelection, selectedCommentId]);

  const queueContextCitation = useCallback(
    (target: CitationTarget, transient: boolean) => {
      if (opts.hasContextAssets && contextCollapsed) {
        pendingCitationRef.current = { ...target, transient };
        onContextCollapsedChange(false);
        return true;
      }
      applyContextCitation({ ...target, transient }, { transient });
      return false;
    },
    [
      applyContextCitation,
      contextCollapsed,
      onContextCollapsedChange,
      opts.hasContextAssets,
    ],
  );

  useEffect(() => {
    if (sourcePlacementMode) return;
    if (!selectedCommentId) return;

    const comment = commentsForSelection.find(
      (c) => c._id === selectedCommentId,
    );
    if (!comment) return;

    const citations = getNavigableCitations(comment);
    const index =
      citations.length === 0
        ? 0
        : Math.min(Math.max(activeCitationIndex, 0), citations.length - 1);

    const prior = autoAppliedSelectionRef.current;
    if (
      prior?.commentId === selectedCommentId &&
      prior.citationIndex === index
    ) {
      return;
    }
    autoAppliedSelectionRef.current = {
      commentId: selectedCommentId,
      citationIndex: index,
    };

    if (citations.length === 0) {
      if (contextHighlightTimerRef.current) {
        clearTimeout(contextHighlightTimerRef.current);
        contextHighlightTimerRef.current = null;
      }
      setContextCitationPin(null);
      setContextHighlight(null);
      setSelectedKnowledgeRecordId(null);
      return;
    }

    const target = citationToTarget(citations[index]);
    if (!target) return;

    if (hasContextAssets && contextCollapsed) {
      pendingCitationRef.current = { ...target, transient: false };
      onContextCollapsedChange(false);
      return;
    }
    applyContextCitation({ ...target, transient: false }, { transient: false });
  }, [
    activeCitationIndex,
    applyContextCitation,
    commentsForSelection,
    contextCollapsed,
    hasContextAssets,
    onContextCollapsedChange,
    selectedCommentId,
    sourcePlacementMode,
  ]);

  const handleCommentClick = useCallback(
    (comment: BaseTicketComment) => {
      const id = comment._id ?? null;
      setActiveCitationIndex(0);
      setSelectedCommentId(id);
      onCommentDeepLinkChange?.(id);
      if (comment.rel_x != null && comment.rel_y != null) {
        if (focusTimerRef.current) clearTimeout(focusTimerRef.current);
        setHighlightPing({
          x: comment.rel_x,
          y: comment.rel_y,
          page: comment.page_num,
        });
        focusTimerRef.current = setTimeout(
          () => setHighlightPing(null),
          COMMENT_HIGHLIGHT_DURATION_MS,
        );
      }
    },
    [onCommentDeepLinkChange],
  );

  const handleCitationClick = useCallback(
    (citation: CitationTarget) => {
      if (citation.knowledgeDataRecordId) {
        queueContextCitation(citation, true);
        return;
      }

      setSelectedKnowledgeRecordId(null);
      queueContextCitation(citation, true);
    },
    [queueContextCitation],
  );

  const handleCommentNavigate = useCallback(
    (dir: 1 | -1) => {
      const idx = commentsForSelection.findIndex(
        (c) => c._id === selectedCommentId,
      );
      if (idx < 0) return;

      const comment = commentsForSelection[idx];
      const citations = getNavigableCitations(comment);

      if (dir === 1) {
        if (
          citations.length > 0 &&
          activeCitationIndex < citations.length - 1
        ) {
          setActiveCitationIndex((i) => i + 1);
          return;
        }
        const next = commentsForSelection[idx + 1];
        if (next) {
          setActiveCitationIndex(0);
          handleCommentClick(next);
          if (next.page_num != null) {
            artworkPaneRef.current?.navigateToPage(next.page_num);
          }
        }
        return;
      }

      if (citations.length > 0 && activeCitationIndex > 0) {
        setActiveCitationIndex((i) => i - 1);
        return;
      }
      const prev = commentsForSelection[idx - 1];
      if (prev) {
        const prevCitations = getNavigableCitations(prev);
        setActiveCitationIndex(Math.max(prevCitations.length - 1, 0));
        handleCommentClick(prev);
        if (prev.page_num != null) {
          artworkPaneRef.current?.navigateToPage(prev.page_num);
        }
      }
    },
    [
      activeCitationIndex,
      commentsForSelection,
      handleCommentClick,
      selectedCommentId,
    ],
  );

  const startContextSourcePlacement = useCallback(
    (onPinComplete?: (citation: TicketMessageCitation) => void) => {
      if (!hasContextAssets) return;
      onContextCollapsedChange(false);
      contextSourcePinCompleteRef.current = onPinComplete ?? null;
      setSourcePlacementMode(true);
      setDraftContextSourcePin(null);
    },
    [hasContextAssets, onContextCollapsedChange],
  );

  const cancelContextSourcePlacement = useCallback(() => {
    setSourcePlacementMode(false);
    setDraftContextSourcePin(null);
    contextSourcePinCompleteRef.current = null;
  }, []);

  const handleContextSourcePin = useCallback(
    (pin: { relX: number; relY: number; page: number; sourceUrl: string }) => {
      const asset =
        contextFiles.find((file) => file.source === pin.sourceUrl) ??
        contextFiles[0];
      if (!asset) return;
      const citation = buildContextFileCitation(asset, pin);
      const onComplete = contextSourcePinCompleteRef.current;
      if (onComplete) {
        onComplete(citation);
      } else {
        setDraftCitations((prev) => [...prev, citation]);
      }
      contextSourcePinCompleteRef.current = null;
      setDraftContextSourcePin({
        relX: pin.relX,
        relY: pin.relY,
        page: pin.page,
      });
      setSourcePlacementMode(false);
    },
    [contextFiles],
  );

  return {
    selectedCommentId,
    highlightPing,
    contextHighlight,
    contextPaneRef,
    artworkPaneRef,
    stableComments,
    detachedCommentPanelProps,
    onDetachedCommentPanelProps,
    artworkHeaderControls,
    setArtworkHeaderControls,
    contextHeaderControls,
    setContextHeaderControls,
    handleCommentClick,
    handleCitationClick,
    handleCommentNavigate,
    activeCitationIndex,
    navigableCitationsForSelected,
    draftCitations,
    setDraftCitations,
    sourcePlacementMode,
    draftContextSourcePin,
    contextCitationPin,
    startContextSourcePlacement,
    cancelContextSourcePlacement,
    handleContextSourcePin,
    selectedKnowledgeRecordId,
    setSelectedKnowledgeRecordId,
    includeRejectedComments,
    setIncludeRejectedComments,
  };
}

export type ReviewArtworkColumnProps = {
  data: ReviewArtworkContextData;
  review: ReturnType<typeof useReviewArtworkContextColumns>;
  artworkTitle?: string;
  artworkHeaderLeading?: ReactNode;
  artworkHeaderTrailing?: ReactNode;
  artworkBetweenHeaderAndBody?: ReactNode;
  artworkCollapseControl?: ReactNode;
  artworkBodyTestId?: string;
  onPinDragEnd?: ReviewArtworkContextColumnsOptions["onPinDragEnd"];
  onPasswordRequired?: ReviewArtworkContextColumnsOptions["onPasswordRequired"];
};

export function ReviewArtworkColumn({
  data,
  review,
  artworkTitle = "Artwork",
  artworkHeaderLeading,
  artworkHeaderTrailing,
  artworkBetweenHeaderAndBody,
  artworkCollapseControl,
  artworkBodyTestId = "campaign-review-artwork-body",
  onPinDragEnd,
  onPasswordRequired,
}: ReviewArtworkColumnProps) {
  const { ticketId, isLoading, latestReviewFiles, comments } = data;
  const {
    stableComments,
    onDetachedCommentPanelProps,
    selectedCommentId,
    highlightPing,
    artworkHeaderControls,
    setArtworkHeaderControls,
    artworkPaneRef,
    handleCommentClick,
    handleCitationClick,
    draftCitations,
    setDraftCitations,
    startContextSourcePlacement,
    cancelContextSourcePlacement,
    sourcePlacementMode,
  } = review;

  if (isLoading) {
    return (
      <div
        className="flex flex-1 items-center justify-center"
        data-testid="campaign-review-loading"
      >
        <CircleNotch
          size={24}
          weight="bold"
          className="animate-spin text-puntt-neutral-gray-9"
        />
      </div>
    );
  }

  return (
    <>
      <PaneHeader
        region="artwork"
        title={artworkTitle}
        leading={artworkHeaderLeading}
        trailing={
          <>
            {artworkHeaderTrailing}
            {artworkHeaderControls}
            {artworkCollapseControl}
          </>
        }
      />
      {artworkBetweenHeaderAndBody}
      <div
        className="flex min-h-0 flex-1 flex-col overflow-hidden"
        data-testid={artworkBodyTestId}
      >
        <div className="min-h-0 flex-1">
          <AssetViewerPane
            key={ticketId}
            ref={artworkPaneRef}
            title=""
            assets={latestReviewFiles}
            comments={stableComments}
            allReviewComments={comments}
            includeRejectedComments={review.includeRejectedComments}
            onIncludeRejectedCommentsChange={review.setIncludeRejectedComments}
            activeCommentId={selectedCommentId}
            onCommentClick={handleCommentClick}
            onPinClick={handleCommentClick}
            onPinDragEnd={onPinDragEnd}
            onCitationClick={handleCitationClick}
            highlightPosition={highlightPing}
            ticketId={ticketId}
            hideCommentPanel
            viewerToolbarPlacement="external"
            onHeaderControlsChange={setArtworkHeaderControls}
            commentComposePlacement="inline"
            onDetachedCommentPanelProps={onDetachedCommentPanelProps}
            onPasswordRequired={onPasswordRequired}
            draftCitations={draftCitations}
            onDraftCitationsChange={setDraftCitations}
            hasContextAssets={data.hasContextAssets}
            onStartContextSourcePlacement={startContextSourcePlacement}
            onCancelSourcePlacement={cancelContextSourcePlacement}
            sourcePlacementActive={sourcePlacementMode}
          />
        </div>
      </div>
    </>
  );
}

type ReviewCommentsContextBodyProps = {
  data: ReviewArtworkContextData;
  review: ReturnType<typeof useReviewArtworkContextColumns>;
  contextBodyTestId?: string;
  commentsEmptyTestId?: string;
  commentsPaneTestId?: string;
  contextAssetPaneTestId?: string;
  onPasswordRequired?: ReviewArtworkContextColumnsOptions["onPasswordRequired"];
  onContextCollapse?: () => void;
  contextCollapseTestId?: string;
};

/**
 * Right-pane body for chat/campaign reviews: comments size to their content
 * (capped at half the column), context/reference assets fill the remainder.
 */
function ReviewCommentsContextBody({
  data,
  review,
  contextBodyTestId = "campaign-review-context-body",
  commentsEmptyTestId = "campaign-review-comments-empty",
  commentsPaneTestId = "review-context-comments-pane",
  contextAssetPaneTestId = "review-context-asset-pane",
  onPasswordRequired,
  onContextCollapse,
  contextCollapseTestId = "campaign-review-context-collapse",
}: ReviewCommentsContextBodyProps) {
  const { ticketId, contextFiles, hasContextAssets } = data;
  const {
    detachedCommentPanelProps,
    contextHighlight,
    contextPaneRef,
    contextHeaderControls,
    setContextHeaderControls,
    sourcePlacementMode,
    draftContextSourcePin,
    contextCitationPin,
    handleContextSourcePin,
    cancelContextSourcePlacement,
    selectedKnowledgeRecordId,
    setSelectedKnowledgeRecordId,
    handleCommentNavigate,
    activeCitationIndex,
    navigableCitationsForSelected,
  } = review;

  const contextCollapseControl = onContextCollapse ? (
    <Tooltip content="Collapse context">
      <PaneHeaderIconButton
        onClick={onContextCollapse}
        aria-label="Collapse context"
        data-testid={contextCollapseTestId}
      >
        <ArrowsInSimple size={PANE_HEADER_ICON_SIZE} aria-hidden="true" />
      </PaneHeaderIconButton>
    </Tooltip>
  ) : null;

  return (
    <div
      className={
        hasContextAssets
          ? "flex min-h-0 flex-1 flex-col overflow-hidden"
          : "flex h-full min-h-0 flex-col overflow-y-auto"
      }
      data-testid={contextBodyTestId}
    >
      <div
        className={
          hasContextAssets
            ? "flex max-h-[50%] min-h-0 shrink-0 flex-col overflow-y-auto bg-base-white"
            : "flex shrink-0 flex-col bg-base-white"
        }
        data-testid={commentsPaneTestId}
      >
        {detachedCommentPanelProps ? (
          <CommentPanel
            {...detachedCommentPanelProps}
            navigableCitations={navigableCitationsForSelected}
            activeCitationIndex={activeCitationIndex}
            onCommentNavigate={handleCommentNavigate}
            commentComposePlacement="inline"
            navTrailing={contextCollapseControl}
            clampCommentSections={hasContextAssets}
            // Side column — not docked under an asset. Avoid panel border-t
            // stacking with the project/page header into a 2px rule.
            fillContainer
          />
        ) : (
          <div
            className={
              hasContextAssets
                ? "flex min-h-0 flex-1 flex-col overflow-hidden"
                : "flex flex-col"
            }
          >
            {contextCollapseControl ? (
              <nav
                aria-label="Comment navigation"
                className="flex h-10 shrink-0 items-center justify-end border-b border-puntt-neutral-gray-6 px-3"
                data-testid="comment-panel-nav"
              >
                <div
                  className="shrink-0"
                  data-testid="comment-panel-nav-trailing"
                >
                  {contextCollapseControl}
                </div>
              </nav>
            ) : null}
            <p
              className="p-4 text-[13px] leading-relaxed text-puntt-neutral-gray-11"
              data-testid={commentsEmptyTestId}
            >
              No comments on this review yet.
            </p>
          </div>
        )}
      </div>
      {hasContextAssets ? (
        <>
          {/* Spreadsheet / image-less viewers often report no toolbar — do not
              leave an empty bordered h-10 band between comments and file tabs. */}
          {contextHeaderControls ? (
            <div
              className="flex h-10 shrink-0 items-center gap-2 border-y border-puntt-neutral-gray-5 px-3"
              data-testid="review-context-asset-toolbar"
            >
              {contextHeaderControls}
            </div>
          ) : null}
          <div
            className={`flex min-h-0 flex-1 flex-col overflow-hidden${
              sourcePlacementMode ? "ring-2 ring-inset ring-puntt-accent-9" : ""
            }${contextHeaderControls ? "" : "border-t border-puntt-neutral-gray-6"}`}
            data-testid={contextAssetPaneTestId}
            {...(sourcePlacementMode
              ? { "data-context-source-placement-active": true }
              : {})}
          >
            {selectedKnowledgeRecordId ? (
              <KnowledgeRecordContextPanel
                recordId={selectedKnowledgeRecordId}
                onClose={() => setSelectedKnowledgeRecordId(null)}
              />
            ) : (
              <AssetViewerPane
                ref={contextPaneRef}
                key={`${ticketId}-context`}
                title=""
                assets={contextFiles}
                hideCommentPanel
                highlightPosition={contextHighlight}
                citationFocusPin={contextCitationPin}
                viewerToolbarPlacement="external"
                viewerContentInset="inset16"
                onHeaderControlsChange={setContextHeaderControls}
                onPasswordRequired={onPasswordRequired}
                sourcePlacementMode={sourcePlacementMode}
                draftSourcePin={draftContextSourcePin}
                onPlaceSourcePin={handleContextSourcePin}
                onCancelSourcePlacement={cancelContextSourcePlacement}
              />
            )}
          </div>
        </>
      ) : selectedKnowledgeRecordId ? (
        <div
          className="flex min-h-0 flex-1 flex-col overflow-hidden border-t border-puntt-neutral-gray-6"
          data-testid={contextAssetPaneTestId}
        >
          <KnowledgeRecordContextPanel
            recordId={selectedKnowledgeRecordId}
            onClose={() => setSelectedKnowledgeRecordId(null)}
          />
        </div>
      ) : null}
    </div>
  );
}

export type ReviewContextColumnProps = {
  data: ReviewArtworkContextData;
  review: ReturnType<typeof useReviewArtworkContextColumns>;
  contextCollapseTestId?: string;
  contextBodyTestId?: string;
  commentsEmptyTestId?: string;
  onPasswordRequired?: ReviewArtworkContextColumnsOptions["onPasswordRequired"];
  onContextCollapse?: () => void;
};

export function ReviewContextColumn({
  data,
  review,
  contextCollapseTestId = "campaign-review-context-collapse",
  contextBodyTestId = "campaign-review-context-body",
  commentsEmptyTestId = "campaign-review-comments-empty",
  onPasswordRequired,
  onContextCollapse,
}: ReviewContextColumnProps) {
  const { isLoading } = data;

  if (isLoading) {
    return (
      <div className="flex flex-1 items-center justify-center">
        <CircleNotch
          size={24}
          weight="bold"
          className="animate-spin text-puntt-neutral-gray-9"
        />
      </div>
    );
  }
  return (
    <ReviewCommentsContextBody
      data={data}
      review={review}
      contextBodyTestId={contextBodyTestId}
      commentsEmptyTestId={commentsEmptyTestId}
      onPasswordRequired={onPasswordRequired}
      onContextCollapse={onContextCollapse}
      contextCollapseTestId={contextCollapseTestId}
    />
  );
}

export type ReviewArtworkContextPanelGroupProps = {
  ticketId: string;
  onClose: () => void;
  storageKey?: string;
  initialCommentId?: string | null;
  onCommentDeepLinkChange?: (commentId: string | null) => void;
  onInvalidCommentDeepLink?: () => void;
};

/**
 * Combined artwork + context layout for the project review screen. Owns
 * internal context collapse state and a resizable PanelGroup.
 */
export function ReviewArtworkContextPanelGroup({
  ticketId,
  onClose,
  storageKey = "askpuntt-project-review-artwork-context",
  initialCommentId,
  onCommentDeepLinkChange,
  onInvalidCommentDeepLink,
}: ReviewArtworkContextPanelGroupProps) {
  const {
    ticket,
    comments,
    isLoading,
    hasContextAssets,
    contextFiles,
    latestReviewFiles,
  } = useTicketViewData(ticketId);

  // The version currently in view = the latest non-deleted revision board. Its
  // per-reviewer `reviews[]` carry the human approval decision the agent
  // records from chat; the badge in the artwork header reflects it.
  const latestBoard = ticket?.revisionBoards
    ?.filter((b) => !b.deletedAt)
    .at(-1);

  const [contextCollapsed, setContextCollapsed] = useState(false);
  const [activeDragId, setActiveDragId] = useState<string | null>(null);
  const panelGroupRef = useRef<ImperativePanelGroupHandle>(null);

  const ARTWORK_CONTEXT_DEFAULT: number[] = [62, 38];
  const ARTWORK_CONTEXT_MINS: number[] = [35, 22];
  const artworkContextLayout = usePersistedLayout({
    storageKey,
    defaultLayout: ARTWORK_CONTEXT_DEFAULT,
    minSizes: ARTWORK_CONTEXT_MINS,
  });

  const dragAbortSignal = `${ticketId}:${contextCollapsed ? "collapsed" : "expanded"}`;
  const dragInProgress = activeDragId !== null;
  const useResizableContextLayout = hasContextAssets && !contextCollapsed;

  const reviewData: ReviewArtworkContextData = {
    ticketId,
    comments,
    isLoading,
    hasContextAssets,
    contextFiles,
    latestReviewFiles,
  };

  const review = useReviewArtworkContextColumns({
    ...reviewData,
    contextCollapsed,
    onContextCollapsedChange: setContextCollapsed,
    initialCommentId,
    onCommentDeepLinkChange,
  });

  useEffect(() => {
    if (!initialCommentId || isLoading || comments == null) return;
    const visible = visibleReviewComments(comments).some(
      (c) => c._id === initialCommentId,
    );
    if (!visible) {
      onInvalidCommentDeepLink?.();
    }
  }, [comments, initialCommentId, isLoading, onInvalidCommentDeepLink]);

  const artworkColumnInner = (
    <ReviewArtworkColumn
      data={reviewData}
      review={review}
      artworkHeaderLeading={
        <ArtifactPaneCloseButton
          onClick={onClose}
          data-testid="campaign-review-close"
          ariaLabel="Close review"
        />
      }
      artworkHeaderTrailing={
        <VersionApprovalBadge reviews={latestBoard?.reviews} />
      }
    />
  );

  const contextColumnInner = (
    <ReviewContextColumn
      data={reviewData}
      review={review}
      onContextCollapse={() => setContextCollapsed(true)}
    />
  );

  return (
    <section
      className="flex h-full min-w-0 flex-1 overflow-hidden"
      data-testid="campaign-review-pane"
      aria-label="Asset review"
    >
      {useResizableContextLayout ? (
        <PanelGroup
          direction="horizontal"
          ref={panelGroupRef}
          onLayout={artworkContextLayout.onLayout}
          className={`flex flex-1 ${dragInProgress ? "[&_*]:!transition-none" : ""}`}
        >
          <Panel
            id="artwork"
            order={0}
            minSize={ARTWORK_CONTEXT_MINS[0]}
            defaultSize={artworkContextLayout.layout[0]}
          >
            <div
              className="flex h-full min-w-0 flex-col overflow-hidden bg-base-white"
              data-testid="pane-artwork"
            >
              {artworkColumnInner}
            </div>
          </Panel>
          <ResizeHandle
            id="context-artwork"
            panelGroupRef={panelGroupRef}
            defaultLayout={ARTWORK_CONTEXT_DEFAULT}
            abortSignal={dragAbortSignal}
            onDraggingChange={(d) =>
              setActiveDragId(d ? "context-artwork" : null)
            }
          />
          <Panel
            id="context"
            order={1}
            minSize={ARTWORK_CONTEXT_MINS[1]}
            defaultSize={artworkContextLayout.layout[1]}
          >
            <aside
              className="flex size-full min-w-0 flex-col overflow-hidden bg-base-white"
              data-testid="pane-context"
              aria-label="Context"
              role="region"
            >
              {contextColumnInner}
            </aside>
          </Panel>
        </PanelGroup>
      ) : !hasContextAssets ? (
        <PanelGroup
          direction="horizontal"
          ref={panelGroupRef}
          onLayout={artworkContextLayout.onLayout}
          className={`flex flex-1 ${dragInProgress ? "[&_*]:!transition-none" : ""}`}
        >
          <Panel
            id="artwork"
            order={0}
            minSize={ARTWORK_CONTEXT_MINS[0]}
            defaultSize={artworkContextLayout.layout[0]}
          >
            <div
              className="flex h-full min-w-0 flex-col overflow-hidden bg-base-white"
              data-testid="pane-artwork"
            >
              {artworkColumnInner}
            </div>
          </Panel>
          <ResizeHandle
            id="context-artwork"
            panelGroupRef={panelGroupRef}
            defaultLayout={ARTWORK_CONTEXT_DEFAULT}
            abortSignal={dragAbortSignal}
            onDraggingChange={(d) =>
              setActiveDragId(d ? "context-artwork" : null)
            }
          />
          <Panel
            id="context"
            order={1}
            minSize={ARTWORK_CONTEXT_MINS[1]}
            defaultSize={artworkContextLayout.layout[1]}
          >
            <aside
              className="flex size-full min-w-0 flex-col overflow-hidden bg-base-white"
              data-testid="pane-context"
              aria-label="Review details"
              role="region"
            >
              <ReviewCommentsContextBody data={reviewData} review={review} />
            </aside>
          </Panel>
        </PanelGroup>
      ) : (
        <>
          <div
            className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden bg-base-white"
            data-testid="campaign-review-artwork-column"
          >
            {artworkColumnInner}
          </div>

          {contextCollapsed && (
            <button
              type="button"
              onClick={() => setContextCollapsed(false)}
              aria-label="Expand context"
              aria-expanded={false}
              data-testid="campaign-review-context-expand"
              className="flex w-8 shrink-0 flex-col items-center gap-2 border-l border-puntt-neutral-gray-5 bg-puntt-neutral-gray-1 py-3 text-puntt-neutral-gray-11 transition-colors hover:bg-puntt-neutral-gray-3 hover:text-puntt-neutral-gray-12"
            >
              <ArrowsOutSimple size={14} aria-hidden="true" />
              <span className="text-[11px] font-bold uppercase tracking-wide [writing-mode:vertical-rl]">
                Context
              </span>
            </button>
          )}
        </>
      )}
    </section>
  );
}
